import { useMemo, useState } from "react"
import { FilePlus2, Link2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import type { LotDetail, LotOTLink } from "@/features/lots/api/types"
import { AssignToOTDialog } from "@/features/lots/components/assign-to-ot-dialog"
import { StageChip } from "@/features/transit/components/stage-chip"
import { LotFillRing } from "@/features/lots/components/lot-fill-ring"
import { lotNetMT } from "@/features/lots/lib/lot-math"
import { LOT_TARGET_BAGS } from "@/features/milling/lib/quality"
import { NEW_OT_LOT_STATE_KEY } from "@/features/transit/lib/new-ot-state"
import { useTransitOrders } from "@/features/transit/hooks/use-transit-orders"
import { paths } from "@/routes/paths"

interface LotStatusActionCardProps {
  lot: LotDetail
}

// Single panel that combines the lot's fill state with the OT
// assignment CTAs for completed lots. The fill is visualised as a
// circular ring on the left; hover the ring for the percentage and
// remaining-bag details so the card body stays quiet.
export function LotStatusActionCard({ lot }: LotStatusActionCardProps) {
  const { t } = useTranslation("lots")
  const navigate = useNavigate()
  const [assignOpen, setAssignOpen] = useState(false)

  const ready = lot.bags >= LOT_TARGET_BAGS
  const netMT = lotNetMT(lot)

  // A lot is bound to its first OT for life — even after that OT is
  // archived or has shipped, it must not be reassigned. Surface the
  // first hydrated link; in normal flow there is at most one.
  const boundLink = useMemo(
    () => (lot.otLinks ?? []).find((link) => link.ot),
    [lot.otLinks],
  )

  // The "Assign to existing OT" CTA appears only when this lot has
  // never been attached to an OT and is COMPLETED. `tab=in_progress`
  // excludes TERMINER on the server; we filter `archived` client-side.
  const canPickOT = !boundLink && lot.status === "COMPLETED"
  const { data: otList } = useTransitOrders({ tab: "in_progress" })
  const eligibleOTCount = (otList ?? []).filter((ot) => !ot.archived).length

  // "Create new OT" navigates to the transit orders page and pops the
  // wizard open there, so the user lands in the OT context with the lot
  // already pre-selected. State is carried via router location instead
  // of a query param so refreshes don't re-open the dialog.
  const createNewOT = () => {
    navigate(paths.transitOrders, {
      state: { [NEW_OT_LOT_STATE_KEY]: lot.id },
    })
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-5">
        <LotFillRing
          current={lot.bags}
          target={LOT_TARGET_BAGS}
          ready={ready}
          lotStatus={lot.status}
          netMT={netMT}
          avgBagKg={lot.avgBagKg ?? null}
        />

        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-8 gap-y-2">
          <KPI
            label={t("detail.summary.netWeight")}
            value={`${netMT.toFixed(3)} MT`}
          />
          <KPI
            label={t("detail.summary.avgBag")}
            value={lot.avgBagKg ? `${lot.avgBagKg.toFixed(2)} kg` : "—"}
          />
        </div>

        {boundLink && boundLink.ot ? (
          <ActiveOTChip link={boundLink} onNavigate={navigate} />
        ) : canPickOT ? (
          <div className="flex flex-shrink-0 items-center gap-2">
            {eligibleOTCount > 0 ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAssignOpen(true)}
              >
                <Link2 className="size-4" />
                {t("detail.summary.assignToExistingOT")}
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={createNewOT}>
                <FilePlus2 className="size-4" />
                {t("detail.summary.createNewOT")}
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {canPickOT ? (
        <AssignToOTDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          lot={lot}
        />
      ) : null}
    </Card>
  )
}

function ActiveOTChip({
  link,
  onNavigate,
}: {
  link: LotOTLink
  onNavigate: (to: string) => void
}) {
  const { t } = useTranslation("lots")
  const ot = link.ot
  if (!ot) return null
  return (
    <button
      type="button"
      onClick={() => onNavigate(paths.transitOrderDetail(ot.id))}
      className="flex flex-shrink-0 items-center gap-3 rounded-md border bg-card px-3 py-2 text-left transition-colors hover:bg-muted/40"
    >
      <div className="flex flex-col">
        <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
          {t("detail.summary.activeOT")}
        </span>
        <span className="font-mono text-sm font-medium">{ot.otNumber}</span>
      </div>
      <StageChip stage={ot.stage} archived={ot.archived} />
    </button>
  )
}

function KPI({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-lg leading-none font-semibold tabular-nums">
        {value}
      </span>
      <span className="mt-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
    </div>
  )
}
